import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fadeUp = {
  initial: { opacity: 0, y: 40 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: "-50px" },
  transition: { duration: 0.6, ease: "easeOut" },
};

export default function Portfolio() {
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    const fetchClients = async () => {
      try {
        const res = await axios.get(`${API}/clients`);
        setClients(res.data || []);
      } catch {
        setClients([]);
      } finally {
        setLoading(false);
      }
    };
    fetchClients();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="min-h-screen pt-20 bg-[#F8F6F3]" data-testid="portfolio-page">
      <section className="py-24 md:py-32">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <motion.div {...fadeUp} className="mb-16">
            <p className="text-xs uppercase tracking-[0.25em] font-semibold text-[#1C1C1C]/40 mb-6">
              § 03 / Our Work
            </p>
            <h2 className="text-4xl md:text-6xl font-black tracking-tight text-[#1C1C1C]">
              Selected <em className="not-italic text-[#FF5E3A]">clients.</em>
            </h2>
          </motion.div>

          {loading ? (
            <div className="flex items-center justify-center py-20">
              <div className="w-8 h-8 border-4 border-[#FF5E3A] border-t-transparent rounded-full animate-spin" />
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
              {clients.map((client, index) => (
                <motion.div
                  key={client.slug}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.08, duration: 0.5 }}
                  className="group cursor-pointer"
                  onClick={() => navigate(`/clients/${client.slug}`)}
                  data-testid={`client-block-${client.slug}`}
                >
                  <div className="bg-white rounded-2xl border border-[#E8E5E0] overflow-hidden transition-all duration-300 hover:border-[#FF5E3A]/30 hover:shadow-xl hover:-translate-y-1">
                    <div className={`aspect-square flex items-center justify-center overflow-hidden ${
                      client.slug === "eurotrader" ? "" : "p-10 bg-white"
                    }`}>
                      {client.logo ? (
                        <img
                          src={client.logo}
                          alt={`${client.name} logo`}
                          className={`transition-transform duration-300 group-hover:scale-105 ${
                            client.slug === "eurotrader"
                              ? "w-full h-full object-cover"
                              : "max-w-[75%] max-h-[75%] object-contain"
                          }`}
                        />
                      ) : (
                        <span className="text-2xl font-black text-[#1C1C1C]/70 text-center tracking-tight">
                          {client.name}
                        </span>
                      )}
                    </div>
                    <div className="px-6 pb-6 pt-2">
                      <div className="flex items-center justify-between">
                        <h3 className="font-bold text-[#1C1C1C]">{client.name}</h3>
                        <ArrowRight size={16} className="text-[#FF5E3A] opacity-0 -translate-x-2 transition-all duration-300 group-hover:opacity-100 group-hover:translate-x-0" />
                      </div>
                      {client.services && client.services.length > 0 && (
                        <div className="flex flex-wrap gap-2 mt-3">
                          {client.services.slice(0, 2).map((service) => (
                            <span key={service} className="text-[10px] font-semibold uppercase tracking-[0.1em] text-[#1C1C1C]/40 bg-[#F8F6F3] px-2 py-1 rounded-full">
                              {service}
                            </span>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          )}
        </div>
      </section>
    </div>
  );
}
